import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import ArticleCard from '../components/ArticleCard';
import articlesData from '../articles.json';

const HomeContainer = styled.div`
  min-height: 100vh;
  background: white;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  padding: var(--space-16) var(--space-4) var(--space-12);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    50% { transform: translate(-30px, -30px) rotate(180deg); }
  }

  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4) var(--space-8);
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: var(--text-5xl);
  font-family: var(--font-serif);
  color: var(--primary-900);
  margin-bottom: var(--space-6);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: var(--text-4xl);
  }
`;

const HeroSubtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--neutral-700);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const TagLine = styled.div`
  margin-top: var(--space-8);
  font-size: var(--text-lg);
  color: var(--primary-700);
  font-weight: 600;
  font-style: italic;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-4);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-8);
`;

const SectionTitle = styled.h2`
  font-size: var(--text-3xl);
  color: var(--neutral-900);
  margin-bottom: var(--space-4);
  font-family: var(--font-serif);
`;

const SectionDescription = styled.p`
  font-size: var(--text-lg);
  color: var(--neutral-700);
  max-width: 600px;
  margin: 0 auto;
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: var(--space-3);
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-8);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--neutral-200);
`;

const FilterButton = styled.button`
  background: ${props => props.$active ? 'var(--primary-700)' : 'white'};
  color: ${props => props.$active ? 'white' : 'var(--neutral-700)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-700)' : 'var(--neutral-300)'};
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: ${props => props.$active ? 'var(--primary-800)' : 'var(--neutral-100)'};
    border-color: ${props => props.$active ? 'var(--primary-800)' : 'var(--neutral-400)'};
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--space-8);
  animation: fadeIn 0.6s ease;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: var(--space-12) var(--space-4);

  h3 {
    font-size: var(--text-2xl);
    color: var(--neutral-700);
    margin-bottom: var(--space-4);
  }

  p {
    font-size: var(--text-lg);
    color: var(--neutral-600);
  }
`;

const InsightBox = styled.div`
  background: var(--primary-50);
  border-left: 4px solid var(--primary-600);
  padding: var(--space-6);
  margin-bottom: var(--space-8);
  border-radius: var(--radius-md);
`;

const InsightTitle = styled.h3`
  font-size: var(--text-lg);
  color: var(--primary-800);
  margin-bottom: var(--space-2);
  font-weight: 600;
`;

const InsightText = styled.p`
  font-size: var(--text-base);
  color: var(--neutral-700);
  margin: 0;
`;

const HomePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const articles = useMemo(() => articlesData.articles || [], []);

  const categories = useMemo(() => {
    const allCategories = ['All', ...new Set(articles.map(article => article.category))];
    return allCategories;
  }, [articles]);

  const filteredArticles = useMemo(() => {
    if (selectedCategory === 'All') {
      return articles;
    }
    return articles.filter(article => article.category === selectedCategory);
  }, [articles, selectedCategory]);

  const latestInsight = articles.length > 0 ? articles[0] : null;

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Where Intellect Meets Analytics</HeroTitle>
          <HeroSubtitle>
            Explore in-depth analysis, emerging trends, and thought-provoking insights across science, technology, society, and culture.
          </HeroSubtitle>
          <TagLine>"Understanding Today, Analyzing Tomorrow"</TagLine>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <SectionHeader>
          <SectionTitle>Latest Analyses</SectionTitle>
          <SectionDescription>
            Curated insights and comprehensive explorations of the ideas shaping our world
          </SectionDescription>
        </SectionHeader>

        {latestInsight && (
          <InsightBox>
            <InsightTitle>Editor's Pick</InsightTitle>
            <InsightText>
              Today's featured analysis: "{latestInsight.title}" - {latestInsight.content[0].body.substring(0, 150)}...
            </InsightText>
          </InsightBox>
        )}

        <CategoryFilter>
          {categories.map(category => (
            <FilterButton
              key={category}
              $active={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </FilterButton>
          ))}
        </CategoryFilter>

        {filteredArticles.length > 0 ? (
          <ArticlesGrid>
            {filteredArticles.map(article => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </ArticlesGrid>
        ) : (
          <NoResultsMessage>
            <h3>No articles found</h3>
            <p>Try selecting a different category</p>
          </NoResultsMessage>
        )}
      </MainContent>
    </HomeContainer>
  );
};

export default HomePage;